import { Observable, Subscription } from 'rxjs';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { HttpRequestDecorator } from 'app/core/triggerbee/http.request.decorator';
import {
  AddCollectionEvent,
  CurrentlySelectedCollection,
  MediaModel,
  MediaProcessingType,
  MediaUploadSettings,
  MaterialIcon,
  MediaCategory,
  MediaCollection,
} from './media-library.model';
import { SnackBarService } from 'app/core/snack-bar.service';
import { MatDialog } from '@angular/material/dialog';
import { AccountFileResponse, WidgetComponent, WidgetComponentSubType } from 'app/campaign/widgets.model';
import { WidgetService } from '../services/widget.service';
import { AccountSettingsService } from 'app/account-settings/account-settings.service';
import { DomSanitizer } from '@angular/platform-browser';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { AccountVariable, AccountVariableType } from 'app/account-settings/account-settings-models';
import { WidgetEditorDataService } from 'app/campaign/widget-editor-data.service';
import { UploadModalComponent, UploadModalData } from './upload-modal/upload-modal.component';
import { FilterIconsPipe } from '../pipes/filter-string.pipe';
import { MatExpansionPanel } from '@angular/material/expansion';
import { TranslateService } from '@ngx-translate/core';
import ColorHelpers from '../helpers/color-helpers';
import { CookieService } from 'app/core/cookie.service';
import { MediaLibraryService } from './media-library.service';
import {
  UploadMediaModalComponent,
  UploadMediaFilesModalData,
  UploadMediaFilesModalResponse,
} from './upload-media-modal/upload-media-modal.component';
import WidgetComponentHelpers from '../../campaign/widget-component-helpers';
import { LanguageService } from '../../core/language.service';
import { AccountService } from '../../account/account.service';
import { Account } from '../account/account.model';

@Component({
  selector: 'app-media-library',
  templateUrl: './media-library.component.html',
  styleUrls: ['./media-library.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MediaLibraryComponent implements OnInit, AfterViewInit, OnDestroy {
  public searchString: string = '';
  public materialIcons: Array<MaterialIcon> = [];
  private materialIconVersion = 1;
  private CACHE_KEY = 'material_icons_v' + this.materialIconVersion;
  public DISPLAY_LIMIT = 128;
  public collections: { [key: string]: MediaCollection[] } = null;
  public MediaCategory = MediaCategory;
  public modal: 'library' | 'upload' = 'library';
  private isDarkMode: boolean = false;

  currentAccount: Account;
  loaded = false;
  dragging = false;
  isLoading = false;
  mediaUrl: string;
  mediaUrlInvalidMessage: string = null;
  prevLoadedImages: Array<any> = [];
  fileName = '';
  file;
  accountVariables: Array<AccountVariable> = [];
  accountColorVariables: Array<AccountVariable> = [];
  uploadCategory = MediaCategory.Photos;
  uploadCollection?: string = null;
  filteredIcons: Array<any> = [];
  images: Array<MediaModel>;
  numSelected = 0;
  supportsFontIcons: boolean;
  supportsVideos: boolean;
  iconColor?: string = null;
  iconColorDisplay: string = 'var(--textColor)';
  iconPanelOpen = false;
  colorError: any;
  collectionsCacheKeyPrefix = 'image_collections_';
  iconColorIsBright: boolean = false;
  filesDraggedOver: boolean = false;
  dragCounter: number = 0;
  public brandSettingColor: string;
  videoMp4: string = 'video/mp4';
  videoWebm: string = 'video/webm';

  currentSelection: CurrentlySelectedCollection = { collection: null, category: MediaCategory.Photos };

  @Input() onMediaSelected: Function;
  @Input() selectingMedia: boolean;
  @Input() defaultCategory: MediaCategory;
  @Input() data: any;
  @Output() close = new EventEmitter<any>();
  @ViewChild('iconPanel') panel: MatExpansionPanel;
  @ViewChild('searchInput') searchInput;
  @Input() hideIcons = false;

  private subscriptions: Subscription[] = [];
  constructor(
    private dialog: MatDialog,
    private widgetService: WidgetService,
    private sanitizer: DomSanitizer,
    private http: HttpRequestDecorator,
    private snackbarService: SnackBarService,
    private widgetEditorDataService: WidgetEditorDataService,
    private accountSettingsService: AccountSettingsService,
    private translateService: TranslateService,
    private cookieService: CookieService,
    private mediaLibraryService: MediaLibraryService,
    private languageService: LanguageService,
    private accountService: AccountService,
  ) {}

  ngOnInit(): void {
    this.supportsFontIcons = this.data.icons;
    this.supportsVideos = this.data.videos;
    this.mediaUrl = this.data?.uri || '';
    this.brandSettingColor = this.data?.brandSettingColor;
    this.mediaLibraryService.getMediaFiles();
    this.subscriptions.push(
      this.accountSettingsService.getAllAccountVariables().subscribe((accountVariables) => {
        this.accountVariables = accountVariables;
        if (accountVariables) {
          this.accountColorVariables = accountVariables.filter((v) => v.type === AccountVariableType.Color);
          this.accountColorVariables.sort((a, b) => (a.key > b.key ? 1 : -1));
        } else {
          this.accountColorVariables = [];
        }
      }),
    );
    if (!this.defaultCategory && this.selectingMedia) {
      this.defaultCategory = MediaCategory.Photos;
    }
    const materialIcon = this.mediaUrl?.startsWith('$icon/material/');
    if (materialIcon && this.mediaUrl.includes('?')) {
      const parts = this.mediaUrl.split('?');
      const varVal = parts[1].replace('color=', '');
      const hexVal = parts[1].replace('color=', '#');
      const isVar = varVal.includes('{');
      this.iconColor = isVar ? varVal : hexVal;
      this.iconColorDisplay = isVar
        ? ColorHelpers.getColorIfAccountVariable(varVal, this.widgetEditorDataService.accountVariables)
        : hexVal;
    } else {
      this.iconColorDisplay =
        ColorHelpers.getColorIfAccountVariable(this.brandSettingColor, this.widgetEditorDataService.accountVariables) ||
        'var(--textColor)';
    }
    this.isDarkMode = document.body.classList.contains('dark-mode');
    if (!this.isDarkMode && this.iconColorDisplay && this.iconColorDisplay.startsWith('#')) {
      this.iconColorIsBright = ColorHelpers.isColorBright(this.iconColorDisplay);
    }
    this.subscriptions.push(
      this.mediaLibraryService.collectionsBS.subscribe((collections) => {
        if (collections) {
          this.collections = collections;
        }
      }),
    );
    this.subscriptions.push(
      this.accountService.getCurrentAccount().subscribe((account) => {
        if (account) {
          this.currentAccount = account;
        }
      }),
    );
  }

  ngAfterViewInit(): void {
    if (this.supportsFontIcons) {
      this.initIcons();
      if (this.defaultCategory == MediaCategory.Icons) {
        this.panel.open();
      }
    }
    setTimeout(() => {
      this.searchInput.nativeElement.focus();
    }, 200);
  }

  private async initIcons(): Promise<void> {
    const cachedIcons = this.loadIconsFromCache();
    if (cachedIcons) {
      this.materialIcons = cachedIcons;
      if (this.materialIcons[0].display === undefined) {
        this.formatDisplayName(this.materialIcons);
        this.saveIconsToCache(this.materialIcons);
      }
      this.filteredIcons = this.materialIcons.slice(0, this.DISPLAY_LIMIT);
      return;
    }
    this.http.get('assets/icons/material.json').subscribe({
      next: (response: Array<MaterialIcon>) => {
        this.materialIcons = response;
        this.formatDisplayName(this.materialIcons);
        this.sortByPopularity(this.materialIcons);
        this.filteredIcons = this.materialIcons.slice(0, this.DISPLAY_LIMIT);
        this.saveIconsToCache(this.materialIcons);
      },
      error: (error) => {
        this.snackbarService.error('generic.error');
        if (this.materialIcons) {
          this.materialIcons = [];
        }
        console.error('Error fetching icons:', error);
      },
    });
  }

  private formatDisplayName(data: Array<MaterialIcon>) {
    for (let i = 0; i < data.length; i++) {
      const icon = data[i];
      const displayName = icon.name.replace(/_/g, ' ');
      icon.display = displayName.charAt(0).toUpperCase() + displayName.slice(1);
    }
  }

  private sortByPopularity(data: Array<MaterialIcon>) {
    data.sort((a, b) => b.popularity - a.popularity);
  }

  private saveIconsToCache(icons: Array<MaterialIcon>): void {
    try {
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(icons));
    } catch (error) {
      console.warn('Failed to save to cache:', error);
    }
  }

  private loadIconsFromCache(): Array<MaterialIcon> | null {
    try {
      const cached = localStorage.getItem(this.CACHE_KEY);
      return JSON.parse(cached);
    } catch (error) {
      console.warn('Failed to load from cache:', error);
    }
    return null;
  }

  public selectMediaFile(mediaFile: MediaModel) {
    if (this.selectingMedia) {
      this.close.emit({ success: true, data: mediaFile });
    } else {
      this.addImageOnClick(mediaFile);
    }
  }

  public selectIcon(icon: MaterialIcon): void {
    let uri = `$icon/material/${icon.name}`;
    if (this.iconColor) {
      uri += `?color=${this.iconColor.replace('#', '')}`;
    }
    this.close.emit({ success: true, data: { uri: uri } });
  }

  public searchMedia(e) {
    if (this.materialIcons) {
      this.filteredIcons = new FilterIconsPipe().transform(this.materialIcons, this.searchString, this.DISPLAY_LIMIT);
    }
  }

  clickOnInput(): void {
    const input = document.getElementById('media') as HTMLInputElement | null;
    if (!input) return;
    input.value = '';
    input.click();
  }

  handleInputChange(e) {
    this.isLoading = true;
    const files = e.dataTransfer
      ? (Array.from(e.dataTransfer.files) as File[])
      : (Array.from(e.target.files) as File[]);
    if (!files || files.length === 0) {
      this.isLoading = false;
      return;
    }

    const unsupportedFiles = files.filter((file) => !this.isSupportedFile(file));
    if (unsupportedFiles.length > 0) {
      this.snackbarService.warning('campaign.widget.sidebar.media.file-format-not-supported');
    }

    const mediaFiles = files.filter((file) => this.isSupportedFile(file));
    if (mediaFiles.length === 0) {
      this.isLoading = false;
      return;
    }
    if (mediaFiles.length === 1) {
      this.handleSingleMediaFile(mediaFiles[0]);
    } else {
      this.handleMultipleMediaFiles(mediaFiles);
    }
  }

  private determineCategoryFromFile(file: File): MediaCategory {
    if (this.isVideo(file)) {
      return MediaCategory.Videos;
    }
    return MediaCategory.Photos;
  }

  private handleSingleMediaFile(file: File) {
    const isImage = file.type.startsWith('image/');
    const isVideo = this.isVideo(file);
    const reader = new FileReader();

    if (!isImage && !isVideo) {
      this.isLoading = false;
      this.snackbarService.warning('campaign.widget.sidebar.media.file-format-not-supported');
      return;
    }

    if (file.size / 1024 > 5120 && (file.type.includes('gif') || file.type.includes('svg'))) {
      this.isLoading = false;
      this.snackbarService.error('campaign.widget.component.image.messages.file-is-too-large');
      return;
    }

    const maxVideoSizeInBytes = 25 * 1024 * 1024; // 25 MB
    if (isVideo && file.size > maxVideoSizeInBytes) {
      this.isLoading = false;
      this.snackbarService.error('campaign.widget.sidebar.media.upload-videos.video-too-large');
      return;
    }

    this.loaded = false;
    this.fileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_'); // sanitize filename
    this.file = file;

    const originalUrl = this.mediaUrl;
    const detectedCategory = this.determineCategoryFromFile(file);
    this.currentSelection = {
      collection: this.currentSelection.collection,
      category: detectedCategory,
    };

    const openModal = () => {
      this.openUploadOptions(file, this.fileName, isVideo).subscribe((result: MediaUploadSettings) => {
        if (!result?.success) {
          this.mediaUrl = originalUrl;
          this.currentSelection.category = MediaCategory.Photos;
          if (result?.error) {
            this.postSideEffectsAfterRejectImage(result.error);
          }
          return;
        }
        this.uploadCategory = result.category;
        this.uploadCollection = result.collection ? result.collection.replace(/ /g, '_') : result.collection;
        if (result.uploadResult) {
          this.postSideEffectsAfterUploadImage(result.uploadResult as AccountFileResponse);
        }
      });
    };

    if (isVideo) {
      openModal();
    } else {
      reader.onload = () => {
        this.mediaUrl = reader.result as string;
        openModal();
      };
      reader.readAsDataURL(file);
    }
  }

  private handleMultipleMediaFiles(files: File[]) {
    const dialogRef = this.dialog.open(UploadMediaModalComponent, {
      width: '800px',
      maxWidth: '90vw',
      data: {
        photoContainers: this.collections[MediaCategory.Photos].filter((c) => !c.isTriggerbee).map((c) => c.name),
        graphicContainers: this.collections[MediaCategory.Graphics].filter((c) => !c.isTriggerbee).map((c) => c.name),
        videoContainers: this.collections[MediaCategory.Videos].filter((c) => !c.isTriggerbee).map((c) => c.name),
        category: this.currentSelection.category,
        collection: this.currentSelection.collection,
        files: files,
        isInBackoffice: false,
        supportsVideos: this.supportsVideos,
      } as UploadMediaFilesModalData,
    });

    dialogRef.afterClosed().subscribe((result: UploadMediaFilesModalResponse) => {
      if (result && result.files && result.files.length > 0) {
        this.mediaLibraryService.addMediaFiles(result.files);
      }
    });
  }

  saveMediaUrl(): void {
    const isLocalDriveUrl = /^[a-z]:/i.test(this.mediaUrl);

    if (isLocalDriveUrl) {
      this.mediaUrlInvalidMessage = 'campaign.widget.component.image.url-validation-message';
      return;
    }

    const urlRegExp = new RegExp('(https?://)?([\\da-z.-]+)\\.([a-z.]{2,6})[/\\w .-]*/?'); // Simple URL validation
    const videoRegExp = new RegExp('\\.(mp4|webm)(\\?.*)?$', 'i'); // Video URL validation

    const accountVariablesVariable = this.mediaUrl.match('{([^|]+?)(?:\\s*\\|\\|\\s*"([^"]*)"\\s*)?}'); // Account variables - single brackets
    const dataRepeaterVariable = this.mediaUrl.match('{{([^|]+?)(?:\\s*\\|\\|\\s*"([^"]*)"\\s*)?}}'); // Data repeater variables - double brackets
    const hasVariable = !!(accountVariablesVariable || dataRepeaterVariable);

    const unsupportedVideoRegExp = new RegExp('\\.(mov|avi|wmv|mkv|flv)(\\?.*)?$', 'i');
    const isUnsupportedVideo = !hasVariable && unsupportedVideoRegExp.test(this.mediaUrl);

    if (isUnsupportedVideo) {
      this.mediaUrlInvalidMessage = 'campaign.widget.sidebar.media.file-format-not-supported';
      return;
    }

    const isVideo = !hasVariable && videoRegExp.test(this.mediaUrl);

    if (isVideo && !this.supportsVideos) {
      this.mediaUrlInvalidMessage = 'campaign.widget.sidebar.media.file-format-not-supported';
      return;
    }

    const isImage = !hasVariable && !isVideo && urlRegExp.test(this.mediaUrl);

    const isValidVariable =
      hasVariable &&
      (this.isValidAccountVariable(accountVariablesVariable) || this.isValidDataSourceVariable(dataRepeaterVariable)); // Invalid when both are invalid AND a variable was detected

    const isValidMediaUrl = isVideo || isImage || isValidVariable;

    if (!isValidMediaUrl) {
      this.mediaUrlInvalidMessage = hasVariable
        ? 'campaign.widget.component.image.url-validation-variable'
        : 'campaign.widget.component.image.url-validation-message';
      return;
    }
    this.mediaUrlInvalidMessage = null;
    this.close.emit({ success: true, data: { uri: this.mediaUrl } });
  }

  private isValidAccountVariable(variable: RegExpMatchArray): boolean {
    return (
      variable &&
      this.accountVariables.some((item) => item.key === variable[1] && item.type === AccountVariableType.Text)
    );
  }

  private isValidDataSourceVariable(variable: RegExpMatchArray): boolean {
    return variable ? true : false; // TODO: Get properties for current data source and ensure it exists
  }

  openUploadOptions(file: File, fileName: string, isVideo: boolean): Observable<any> {
    const dialogRef = this.dialog.open(UploadModalComponent, {
      maxWidth: '50vw',
      minWidth: '300px',
      data: {
        mediaUrl: this.mediaUrl,
        collections: this.collections,
        currentSelection: this.currentSelection,
        supportsVideos: this.supportsVideos,
        file: file,
        fileName: fileName,
        isVideo: isVideo,
      } as UploadModalData,
    });
    return dialogRef.afterClosed();
  }

  handleSizeFile() {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      maxWidth: '600px',
      minWidth: '300px',
      panelClass: 'confirm-dialog-panel',
      data: {
        text: 'campaign.widget.component.image.confirm-size-img.text',
        proceedButtonText: 'campaign.widget.component.image.confirm-size-img.btn-confirm',
        cancelButtonText: 'campaign.widget.component.image.confirm-size-img.btn-cancel',
      },
    });

    dialogRef.afterClosed().subscribe((resultConfirm) => {
      if (resultConfirm === undefined) {
        this.isLoading = false;
        return;
      }
      const formData = new FormData();
      formData.set('file', this.file, this.fileName);
      formData.append(
        'mediaProcessingType',
        resultConfirm ? MediaProcessingType.Resized : MediaProcessingType.MaxSized,
      );

      const request$: Observable<AccountFileResponse> = this.widgetService.uploadImage(
        formData,
        this.uploadCategory,
        this.uploadCollection,
      );
      request$.subscribe(
        (uploadResult) => this.postSideEffectsAfterUploadImage(uploadResult, resultConfirm),
        (error) => this.postSideEffectsAfterRejectImage(error),
      );
    });
  }

  private postSideEffectsAfterUploadImage(uploadResult: AccountFileResponse, resultConfirm?) {
    this.loaded = true;
    this.isLoading = false;
    this.mediaLibraryService.addMedia(uploadResult);
    if (this.selectingMedia) {
      this.close.emit({ success: true, data: uploadResult });
    }
  }

  private postSideEffectsAfterRejectImage(error) {
    const tooLarge = error.message.includes('Too Large');
    this.close.emit({ success: false, tooLarge });
  }

  private uploadMediaFile(isVideo: boolean) {
    const formData = new FormData();
    formData.set('file', this.file, this.fileName);
    if (!isVideo) {
      formData.append('mediaProcessingType', MediaProcessingType.Original);

      this.widgetService.uploadImage(formData, this.uploadCategory, this.uploadCollection).subscribe(
        (uploadResult) => this.postSideEffectsAfterUploadImage(uploadResult),
        (error) => this.postSideEffectsAfterRejectImage(error),
      );
    } else {
      formData.append('mediaProcessingType', MediaProcessingType.Video);
      this.widgetService.uploadVideo(formData, this.uploadCollection).subscribe(
        (uploadResult) => this.postSideEffectsAfterUploadImage(uploadResult),
        (error) => this.postSideEffectsAfterRejectImage(error),
      );
    }
  }

  public changeOnColorPicker(color: string) {
    this.iconColor = color;
    this.iconColorDisplay = ColorHelpers.getColorIfAccountVariable(this.iconColor, this.accountColorVariables);
    if (!this.isDarkMode && this.iconColorDisplay && this.iconColorDisplay.startsWith('#')) {
      this.iconColorIsBright = ColorHelpers.isColorBright(this.iconColorDisplay);
    } else {
      this.iconColorIsBright = false;
    }
    this.validateColor();
  }

  public validateColor() {
    this.iconColor = this.iconColor.trim();
    this.colorError = undefined;
    if (!this.iconColor) {
      this.colorError = this.translateService.instant('generic.validation.error-required');
    } else {
      const isHexColor = ColorHelpers.validateHexColor(this.iconColor);
      let isRgbaColor = false;
      if (!isHexColor) {
        isRgbaColor = ColorHelpers.validateRgbaColor(this.iconColor);
      }

      if (!isHexColor && !isRgbaColor) {
        this.colorError = this.translateService.instant('generic.validation.error-invalid-color-code');
      }
    }
  }

  public addCollection(e: AddCollectionEvent) {
    this.mediaLibraryService.addCollection(e.collection, e.category);
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  private isVideo(file: File): boolean {
    return file.type === this.videoMp4 || file.type === this.videoWebm;
  }

  private isSupportedFile(file: File): boolean {
    if (file.type.startsWith('image/')) return true;
    if (this.supportsVideos && this.isVideo(file)) return true;
    return false;
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    const files = Array.from(event.dataTransfer?.files || []);

    if (files.every((file) => this.isSupportedFile(file))) {
      this.filesDraggedOver = true;
    }
  }

  onDragEnter(event: DragEvent) {
    event.preventDefault();
    this.dragCounter++;
    this.filesDraggedOver = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.dragCounter--;

    if (this.dragCounter === 0) {
      this.filesDraggedOver = false;
    }
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.filesDraggedOver = false;

    const files = Array.from(event.dataTransfer?.files || []);
    const unsupportedFiles = files.filter((file) => !this.isSupportedFile(file));
    if (unsupportedFiles.length > 0) {
      this.snackbarService.warning('campaign.widget.sidebar.media.file-format-not-supported');
    }

    const mediaFiles = files.filter((file) => this.isSupportedFile(file));

    if (mediaFiles.length === 0) {
      return;
    }
    if (mediaFiles.length === 1) {
      this.isLoading = true;
      this.handleSingleMediaFile(mediaFiles[0]);
    } else if (mediaFiles.length > 1) {
      this.handleMultipleMediaFiles(mediaFiles);
    }
  }

  addImageOnClick(image: MediaModel) {
    if (image.uri.includes('triggerbee/images/')) {
      this.widgetService.copyMediaCollectionImage(image.uri).subscribe((response) => {
        if (response) {
          const componentToAdd = WidgetComponentHelpers.createBlankWidgetComponentFromSubtype(
            this.widgetEditorDataService.widget,
            WidgetComponentSubType.ImageRegular,
            this.languageService,
            false,
            this.currentAccount,
            { uri: response.uri, fileName: image.filename },
          );
          this.addImageToCanvas(componentToAdd);
        }
      });
    } else {
      const componentToAdd = WidgetComponentHelpers.createBlankWidgetComponentFromSubtype(
        this.widgetEditorDataService.widget,
        WidgetComponentSubType.ImageRegular,
        this.languageService,
        false,
        this.currentAccount,
        { uri: image.uri, fileName: image.filename },
      );
      this.addImageToCanvas(componentToAdd);
    }
  }

  private addImageToCanvas(component: WidgetComponent) {
    this.widgetEditorDataService
      .addComponentToWidgetState(component, undefined, undefined, this.currentAccount, false, undefined)
      .subscribe();
  }

  public collectionSelected(currentSelection: CurrentlySelectedCollection) {
    if (currentSelection.collection === 'all' || currentSelection.collection.includes('Triggerbee ')) {
      currentSelection.collection = null;
      this.currentSelection = currentSelection;
    } else {
      this.currentSelection = currentSelection;
    }
  }
}
